/**
 * Saisie d'une question, selon son type.
 *
 * Le type gouverne le formulaire : une question à choix demande des
 * propositions, une question libre des réponses attendues, une question ouverte
 * ni l'une ni l'autre. Afficher les trois et laisser l'enseignant deviner
 * lesquelles comptent produirait des saisies que le serveur refuserait — et il
 * les refuse, `set_choices` sur une question libre étant une erreur.
 *
 * Le type se choisit à la création et **ne se change plus** : côté serveur il
 * est figé (docs/11 §5), et proposer un menu qui échouerait serait pire que ne
 * pas le proposer.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { QuestionType } from "@/features/catalog/api/useCatalog";
import {
  emptyDraft,
  hasAnswerKeys,
  hasChoices,
  type QuestionDraft,
} from "@/features/catalog/lib/questions";
import { isEmptyDocument } from "@/shared/richtext/document";
import { RichTextEditor } from "@/shared/richtext/RichTextEditor";
import { Button } from "@/shared/ui/Button";

const FIELD_CLASSES =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm " +
  "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

/** Les types que l'éditeur propose, dans l'ordre où on les utilise. */
const TYPES: QuestionType[] = [
  "single_choice",
  "multiple_choice",
  "true_false",
  "short_text",
  "number",
  "open_text",
];

interface QuestionFormProps {
  draft: QuestionDraft;
  onChange: (draft: QuestionDraft) => void;
  onSubmit: () => void;
  submitLabel: string;
  disabled?: boolean;
  /** Le type ne se change qu'à la création — jamais sur une question existante. */
  allowTypeChange?: boolean;
}

export function QuestionForm({
  draft,
  onChange,
  onSubmit,
  submitLabel,
  disabled = false,
  allowTypeChange = true,
}: QuestionFormProps) {
  const { t } = useTranslation("catalog");
  const [typeId] = useState(() => `question-type-${Math.random().toString(36).slice(2, 10)}`);

  function setType(type: QuestionType) {
    // Repartir d'un brouillon neuf en gardant l'énoncé : les propositions d'un
    // QCM n'ont aucun sens sur une question ouverte, et les garder en mémoire
    // les enverrait au serveur si l'enseignant revenait au type précédent.
    onChange({ ...emptyDraft(type), prompt: draft.prompt, explanation: draft.explanation });
  }

  return (
    <div className="space-y-3">
      {allowTypeChange && (
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor={typeId}>
            {t("exercises.questionType")}
          </label>
          <select
            id={typeId}
            className={FIELD_CLASSES}
            value={draft.type}
            onChange={(event) => {
              setType(event.target.value as QuestionType);
            }}
          >
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`exercises.types.${type}`)}
              </option>
            ))}
          </select>
        </div>
      )}

      <RichTextEditor
        label={t("exercises.prompt")}
        content={draft.prompt}
        onChange={(prompt) => {
          onChange({ ...draft, prompt });
        }}
        disabled={disabled}
        minHeight="min-h-24"
      />

      {hasChoices(draft.type) && (
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{t("exercises.choices")}</legend>
          {draft.choices.map((choice, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type={draft.type === "multiple_choice" ? "checkbox" : "radio"}
                name={`${typeId}-correct`}
                checked={choice.is_correct}
                aria-label={t("exercises.markCorrect")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  onChange({
                    ...draft,
                    choices: draft.choices.map((item, position) => {
                      if (position === index) {
                        return { ...item, is_correct: checked };
                      }
                      // Un `single_choice` n'a qu'une bonne réponse (INV-C5) :
                      // cocher la seconde décoche la première, plutôt que de
                      // laisser l'enseignant produire un état que la
                      // publication refusera.
                      return draft.type === "multiple_choice"
                        ? item
                        : { ...item, is_correct: false };
                    }),
                  });
                }}
              />
              <input
                className={FIELD_CLASSES}
                value={choice.label}
                aria-label={t("exercises.choiceLabel", { index: index + 1 })}
                onChange={(event) => {
                  onChange({
                    ...draft,
                    choices: draft.choices.map((item, position) =>
                      position === index ? { ...item, label: event.target.value } : item,
                    ),
                  });
                }}
              />
              <Button
                variant="ghost"
                disabled={draft.choices.length <= 2}
                aria-label={t("exercises.removeChoice")}
                onClick={() => {
                  onChange({
                    ...draft,
                    choices: draft.choices.filter((_item, position) => position !== index),
                  });
                }}
              >
                ×
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            onClick={() => {
              onChange({
                ...draft,
                choices: [...draft.choices, { label: "", is_correct: false }],
              });
            }}
          >
            {t("exercises.addChoice")}
          </Button>
        </fieldset>
      )}

      {hasAnswerKeys(draft.type) && (
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{t("exercises.answerKeys")}</legend>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t("exercises.answerKeysHint")}
          </p>
          {draft.answerKeys.map((key, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                className={FIELD_CLASSES}
                value={key.value}
                aria-label={t("exercises.answerKeyLabel", { index: index + 1 })}
                onChange={(event) => {
                  onChange({
                    ...draft,
                    answerKeys: draft.answerKeys.map((item, position) =>
                      position === index ? { ...item, value: event.target.value } : item,
                    ),
                  });
                }}
              />
              <Button
                variant="ghost"
                disabled={draft.answerKeys.length <= 1}
                aria-label={t("exercises.removeAnswerKey")}
                onClick={() => {
                  onChange({
                    ...draft,
                    answerKeys: draft.answerKeys.filter((_item, position) => position !== index),
                  });
                }}
              >
                ×
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            onClick={() => {
              onChange({
                ...draft,
                answerKeys: [...draft.answerKeys, { value: "", match_mode: "exact" }],
              });
            }}
          >
            {t("exercises.addAnswerKey")}
          </Button>
        </fieldset>
      )}

      {/* Une entrée d'une seule ligne pour deux mille caractères de prose : on
          n'y relit jamais ce qu'on vient d'écrire. C'est un champ que l'élève
          lit après s'être trompé — il mérite la même saisie que l'énoncé. */}
      <RichTextEditor
        label={t("exercises.explanation")}
        content={draft.explanation}
        onChange={(explanation) => {
          onChange({ ...draft, explanation });
        }}
        hint={t("exercises.explanationHint")}
        disabled={disabled}
        minHeight="min-h-24"
      />

      <Button disabled={disabled || isEmptyDocument(draft.prompt)} onClick={onSubmit}>
        {submitLabel}
      </Button>
    </div>
  );
}
